// Admin -> Communication -> Messaging (was "SMS Gateway"; grants unchanged).
//
// One gateway (one MSG91 account), two channels, one delivery log:
//
//   SMS          -> Connection, Templates   (admin:sms-gateway)
//   WhatsApp     -> Connection, Templates   (admin:whatsapp-gateway)
//   Notifications -> every message the app sends by itself, its template on
//                    each channel, and an on/off switch per channel (either
//                    grant; each channel's column needs that channel's grant)
//   Delivery log -> All / SMS only / WhatsApp only
//
// Each channel section is shown only to a role holding that channel's grant —
// the backend gates its endpoints the same way, so a hidden section is never
// one that would only have returned 403s. Both tab levels are part of the URL
// (/admin/communication/messaging/<channel>/<connection|templates>) — see
// ADMIN_SUBTABS in adminRegistry.ts.
import AdminTabs from '../AdminTabs';
import { useAdminSubTab } from '../adminRoute';
import { useAuth } from '@/hooks/useAuth';
import ConnectionTab from './ConnectionTab';
import TemplatesTab from './TemplatesTab';
import DeliveryLogTab from './DeliveryLogTab';
import NotificationsTab from './NotificationsTab';
import WhatsAppConnectionTab from '../whatsapp-gateway/ConnectionTab';
import WhatsAppTemplatesTab from '../whatsapp-gateway/TemplatesTab';

export default function SmsGatewayTab() {
  const { user } = useAuth();
  const canSms = !!user?.modules?.includes('admin:sms-gateway');
  const canWhatsApp = !!user?.modules?.includes('admin:whatsapp-gateway');

  // Each channel only to its own grant; Notifications and the log to either.
  const allowed = [...(canSms ? ['sms'] : []), ...(canWhatsApp ? ['whatsapp'] : []), 'notifications', 'log'];
  const tabs = useAdminSubTab('messaging', [], allowed);
  const section = tabs.active;

  return (
    <div className="fade-in">
      <AdminTabs state={tabs} />

      {section === 'sms' && <ChannelTabs channel="sms" />}
      {section === 'whatsapp' && <ChannelTabs channel="whatsapp" />}

      {section === 'notifications' && <NotificationsTab canSms={canSms} canWhatsApp={canWhatsApp} />}

      {section === 'log' && <DeliveryLogTab canSms={canSms} canWhatsApp={canWhatsApp} />}
    </div>
  );
}

/** Connection / Templates inside one channel — the third URL level. */
function ChannelTabs({ channel }: { channel: 'sms' | 'whatsapp' }) {
  const tabs = useAdminSubTab('messaging', [channel]);
  const sms = channel === 'sms';
  return (
    <>
      <AdminTabs state={tabs} level={1} />
      {tabs.active === 'connection' && (sms ? <ConnectionTab /> : <WhatsAppConnectionTab />)}
      {tabs.active === 'templates' && (sms ? <TemplatesTab /> : <WhatsAppTemplatesTab />)}
    </>
  );
}
